import React, { useState, useCallback, useContext } from 'react';
import { saveOverrideV3, saveBulkOverridesV3, updateDriverAssignmentV3, commitAiDriverBuild, createAdjustment, saveDepartmentDriver, calculateForecast } from '../../modules/forecast-v3/api';
import ForecastV3Context from '../../modules/forecast-v3/context/ForecastV3Context';
import { ActionCard, Button, Checkbox } from './response/primitives';
import ForecastChangeHistory from './ForecastChangeHistory';
import './ForecastChangeCard.css';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatCurrency(amount) {
  if (amount == null || isNaN(amount)) return '-';
  const rounded = Math.round(amount);
  const prefix = rounded < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(rounded).toLocaleString()}`;
}

const ForecastChangeCard = ({ proposal, hotelId, orgId = null }) => {
  // Dispatch TRIGGER_REFRESH to the ForecastV3 context after apply (if rendered inside the provider)
  const forecastCtx = useContext(ForecastV3Context);

  const [selected, setSelected] = useState(() => {
    // Default all changes to selected
    const initial = {};
    (proposal.changes || []).forEach((_, idx) => {
      initial[idx] = true;
    });
    return initial;
  });
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null); // { applied: number, failed: number, errors: [] }
  const [dismissed, setDismissed] = useState(false);
  // What this apply wrote, so the change history can be read back from the
  // audit trail afterwards: when it started, and which accounts it touched.
  // Kept in state rather than a ref because the applied card re-renders from it.
  const [applyScope, setApplyScope] = useState(null); // { since, accountIds, year }

  // Filter out no-op changes where the proposed amount equals the current amount
  const changes = (proposal.changes || []).filter(change => {
    if (change.type === 'override' && change.previous_amount != null) {
      return Math.round(parseFloat(change.amount)) !== Math.round(parseFloat(change.previous_amount));
    }
    return true; // keep non-override changes and overrides without previous_amount
  });
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allSelected = selectedCount === changes.length;
  const noneSelected = selectedCount === 0;

  const toggleAll = useCallback(() => {
    const newVal = !allSelected;
    const next = {};
    changes.forEach((_, idx) => {
      next[idx] = newVal;
    });
    setSelected(next);
  }, [allSelected, changes]);

  const toggleOne = useCallback((idx) => {
    setSelected(prev => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  // Extract useful error message from Axios errors
  const getErrorMessage = (err) => {
    if (err.response?.data?.error) return err.response.data.error;
    if (err.response?.data?.message) return err.response.data.message;
    return err.message || 'Unknown error';
  };

  const handleApply = async () => {
    if (noneSelected || applying || results) return;

    setApplying(true);
    // Stamped BEFORE the first write so no event written by this apply can
    // fall outside the window.
    const applyStartedAt = new Date().toISOString();
    const touchedAccountIds = new Set();
    const locationId = parseInt(hotelId);
    let applied = 0;
    let failed = 0;
    const errors = [];

    // Ensure version_id is a valid integer
    const versionId = parseInt(proposal.version_id);
    if (!versionId) {
      setResults({ applied: 0, failed: changes.length, errors: ['No valid forecast version ID in proposal'] });
      setApplying(false);
      return;
    }

    // Track driver assignments already applied to avoid redundant calls
    const appliedDrivers = new Set();

    // A full driver build covers every driverless account — 150+ sequential
    // PUTs would take minutes and half-apply on any hiccup. Send those in one
    // transactional bulk request (same endpoint the grid's bulk edit uses);
    // small hand-authored proposals keep the per-change path.
    const selectedDriverChanges = changes.filter((c, i) => selected[i] && c.type === 'driver');
    const useBulkDrivers = selectedDriverChanges.length > 3;

    if (useBulkDrivers) {
      const assignments = [];
      const seenAccountIds = new Set();
      for (const change of selectedDriverChanges) {
        const chartOfAccountId = parseInt(change.chart_of_account_id);
        if (!chartOfAccountId || seenAccountIds.has(chartOfAccountId)) continue;
        seenAccountIds.add(chartOfAccountId);
        touchedAccountIds.add(chartOfAccountId);
        assignments.push({
          chartOfAccountId,
          driverType: change.driver_type,
          driverInput: change.driver_input ?? 0,
          ...(change.selected_departments?.length > 0 && { selectedDepartments: change.selected_departments }),
          ...(change.labor_position_configs && { laborPositionConfigs: change.labor_position_configs }),
        });
      }

      const CHUNK = 500; // endpoint caps at 2500 per request
      for (let start = 0; start < assignments.length; start += CHUNK) {
        const chunk = assignments.slice(start, start + CHUNK);
        try {
          // aiBuildCommit, not bulkDriver: it also persists the budget
          // build's rooms baseline (room_stat_seed) and splits OTB picks
          // into department drivers — the plain bulk writer does neither,
          // which is how a build's $/ORN drivers applied against zero room
          // nights. Seed rides on the first chunk only.
          const res = await commitAiDriverBuild({
            versionId,
            assignments: chunk,
            roomStatSeed: start === 0 ? (proposal.room_stat_seed || null) : null,
          });
          applied += res?.updated ?? chunk.length;
          failed += res?.failed ?? 0;
          (res?.results || [])
            .filter(r => r.status === 'error')
            .forEach(r => errors.push(`Account ${r.chartOfAccountId}: ${r.error || 'driver assignment failed'}`));
        } catch (err) {
          failed += chunk.length;
          errors.push(`Bulk driver assignment (${chunk.length} accounts): ${getErrorMessage(err)}`);
        }
      }
    }

    for (let i = 0; i < changes.length; i++) {
      if (!selected[i]) continue;
      const change = changes[i];
      if (useBulkDrivers && change.type === 'driver') continue; // applied in the bulk request above
      const touchedId = parseInt(change.chart_of_account_id);
      if (Number.isFinite(touchedId)) touchedAccountIds.add(touchedId);

      try {
        if (change.type === 'override') {
          const month = parseInt(change.month);
          const year = parseInt(change.year) || new Date().getFullYear();
          const chartOfAccountId = parseInt(change.chart_of_account_id);
          const amount = parseFloat(change.amount);

          if (!month || !chartOfAccountId || isNaN(amount)) {
            throw new Error(`Missing required fields: month=${change.month}, accountId=${change.chart_of_account_id}, amount=${change.amount}`);
          }

          await saveOverrideV3({
            versionId,
            chartOfAccountId,
            year,
            month,
            overrideAmount: amount,
            notes: change.reason || 'Applied via AI Assistant'
          });
        } else if (change.type === 'driver') {
          // Deduplicate: drivers apply to all months, so only call once per account
          const driverKey = `${change.chart_of_account_id}:${change.driver_type}`;
          if (appliedDrivers.has(driverKey)) {
            applied++;
            continue;
          }
          appliedDrivers.add(driverKey);
          await updateDriverAssignmentV3({
            versionId,
            chartOfAccountId: parseInt(change.chart_of_account_id),
            driverType: change.driver_type,
            driverInput: change.driver_input ?? 0,
            ...(change.selected_departments?.length > 0 && { selectedDepartments: change.selected_departments }),
          });
        } else if (change.type === 'department_driver') {
          const deptDriverKey = `${change.department_name}:${change.account_type}:${change.driver_type}`;
          if (appliedDrivers.has(deptDriverKey)) {
            applied++;
            continue;
          }
          appliedDrivers.add(deptDriverKey);
          await saveDepartmentDriver({
            versionId,
            locationId,
            departmentName: change.department_name,
            accountType: change.account_type || 'Revenue',
            driverType: change.driver_type,
            driverInput: change.driver_input ?? 0,
            otbLeadDays: change.otb_lead_days ?? -5,
            fallbackDriverType: change.fallback_driver_type || null,
            cascadeOptions: {
              clearAccountDrivers: change.cascade_clear_account_drivers ?? true,
              clearAccountOverrides: false,
              clearAccountAdjustments: false,
              clearAccountNotes: false
            }
          });
        } else if (change.type === 'department_override') {
          // Set a department total for a specific month — spreads across ALL accounts pro rata
          // using budget amounts as the basis (same as PnL grid department cell edit)
          const month = parseInt(change.month);
          const year = parseInt(change.year) || new Date().getFullYear();
          const targetAmount = parseFloat(change.amount);
          const deptFilter = change.department_name;
          const orgId = parseInt(localStorage.getItem('currentOrgId') || '0');

          if (!month || !deptFilter || isNaN(targetAmount)) {
            throw new Error(`Missing required fields for department_override: month=${change.month}, department=${change.department_name}, amount=${change.amount}`);
          }

          // Get all accounts in this department via calculate_forecast
          const monthKey = `${year}-${String(month).padStart(2, '0')}`;
          const calcResults = await calculateForecast({
            orgId,
            locationId,
            versionId,
            chartOfAccountIds: [], // empty = all accounts
            months: [monthKey]
          });

          // Filter to accounts in the target department
          const allResults = calcResults?.results || calcResults || [];
          // Match by department - the AI provides department_name which matches the department in the PnL
          const deptAccounts = allResults.filter(r =>
            r.departmentName === deptFilter || r.department === deptFilter
          );

          if (deptAccounts.length === 0) {
            throw new Error(`No accounts found in department "${deptFilter}" for ${monthKey}`);
          }

          // Use budget as basis for pro-rata (same as PnL grid spread logic)
          const basisValues = deptAccounts.map(r => Math.abs(r.budget || 0));
          const totalBasis = basisValues.reduce((s, v) => s + v, 0);

          // Calculate allocations — spread target pro-rata by budget, equal split if no budget
          const allocations = deptAccounts.map((r, i) => ({
            chartOfAccountId: r.chartOfAccountId,
            amount: totalBasis === 0
              ? targetAmount / deptAccounts.length
              : (basisValues[i] / totalBasis) * targetAmount,
          }));

          // Apply override to EVERY account in the department (zeroes out accounts with $0 allocation)
          const overrideNotes = change.reason || `Department total override: ${deptFilter} → ${formatCurrency(targetAmount)}`;
          await saveBulkOverridesV3(versionId, allocations.map(({ chartOfAccountId, amount }) => ({
            chartOfAccountId,
            year,
            month,
            overrideAmount: Math.round(amount * 100) / 100,
            notes: overrideNotes
          })));
        } else if (change.type === 'adjustment') {
          const year = parseInt(change.year) || new Date().getFullYear();
          await createAdjustment({
            versionId,
            chartOfAccountId: parseInt(change.chart_of_account_id),
            locationId,
            adjustmentType: change.adjustment_type,
            value: change.value,
            description: change.reason || '',
            startMonth: `${year}-${String(change.start_month).padStart(2, '0')}-01`,
            endMonth: `${year}-${String(change.end_month).padStart(2, '0')}-01`,
          });
        }
        applied++;
      } catch (err) {
        failed++;
        errors.push(`${change.account_name}: ${getErrorMessage(err)}`);
      }
    }

    // No auto-zero logic needed — department_override changes handle this via
    // the spread logic in the loop above (calculates all account allocations)

    setResults({ applied, failed, errors });
    // Only worth reading the trail back when something actually landed.
    if (applied > 0) {
      setApplyScope({ since: applyStartedAt, accountIds: [...touchedAccountIds] });
    }
    setApplying(false);

    // Trigger PnL grid refresh if any changes were applied successfully
    if (applied > 0 && forecastCtx?.dispatch) {
      forecastCtx.dispatch({ type: 'TRIGGER_REFRESH' });
    }
  };

  // One proposal row per change: name + from→to source line, current,
  // suggested, change. Mirrors the design's "Update forecast drivers" card.
  const describeChange = (change) => {
    const monthNum = parseInt(change.month);
    const monthLabel = monthNum >= 1 && monthNum <= 12 ? MONTH_NAMES[monthNum - 1] : (change.month ? `M${change.month}` : '');
    const row = { name: change.account_name || change.department_name || 'Change', meta: [], from: null, to: null, current: null, suggested: null, delta: null, deltaKind: 'money' };
    if (change.account_number) row.meta.push(change.account_number);
    if (change.type === 'override' || change.type === 'department_override') {
      row.meta.push(change.type === 'department_override' ? `${monthLabel} · department total` : monthLabel);
      row.current = change.previous_amount != null ? Number(change.previous_amount) : null;
      row.suggested = Number(change.amount);
      if (row.current != null && Number.isFinite(row.suggested)) row.delta = row.suggested - row.current;
      row.from = change.previous_amount != null ? 'Current forecast' : null;
      row.to = 'Override';
    } else if (change.type === 'driver' || change.type === 'department_driver') {
      row.meta.push(change.type === 'department_driver' ? `${change.department_name || ''} driver`.trim() : 'Driver');
      row.from = change.previous_driver_type || null;
      row.to = `${change.driver_type || ''}${change.driver_input != null && change.driver_input !== '' ? ` @ ${change.driver_input}` : ''}`.trim();
      row.currentLabel = change.previous_driver_type || '—';
      row.suggestedLabel = row.to || '—';
    } else if (change.type === 'adjustment') {
      const startLabel = change.start_month ? MONTH_NAMES[change.start_month - 1] : '';
      const endLabel = change.end_month ? MONTH_NAMES[change.end_month - 1] : '';
      row.meta.push(startLabel === endLabel ? startLabel : `${startLabel}–${endLabel}`);
      row.to = change.adjustment_type === 'FLAT' ? 'Flat adjustment' : 'Percent adjustment';
      row.suggestedLabel = change.adjustment_type === 'FLAT' ? formatCurrency(change.value) : `${change.value}%`;
      row.currentLabel = '—';
    }
    return row;
  };

  const signedCurrency = (v) => (v == null || isNaN(v) ? '—' : `${v > 0 ? '+' : v < 0 ? '-' : ''}$${Math.abs(Math.round(v)).toLocaleString()}`);
  const rows = changes.map(describeChange);
  const selectedRows = rows.filter((_, i) => selected[i]);
  const netDelta = selectedRows.reduce((sum, r) => sum + (Number.isFinite(r.delta) ? r.delta : 0), 0);
  const hasMoney = rows.some(r => Number.isFinite(r.delta));
  const title = proposal.title || 'Update the forecast';
  const sub = [proposal.version_name, `${changes.length} change${changes.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ');

  if (results) {
    const tone = results.failed > 0 ? (results.applied > 0 ? 'success' : 'error') : 'success';
    // What was actually committed, read back from the forecast audit trail
    // rather than echoed from the request — a change that failed would
    // otherwise still be listed here as though it had landed.
    const historyDisclosure = applyScope ? {
      label: 'View change history',
      closeLabel: 'Hide change history',
      content: (
        <ForecastChangeHistory
          orgId={orgId}
          locationId={parseInt(hotelId)}
          versionId={parseInt(proposal.version_id)}
          since={applyScope.since}
          accountIds={applyScope.accountIds}
        />
      )
    } : null;
    return (
      <ActionCard
        icon="trend"
        title={title}
        sub={sub}
        pill={{ state: results.failed > 0 && results.applied === 0 ? 'error' : 'done', label: results.failed > 0 && results.applied === 0 ? 'Failed' : 'Applied' }}
        disclosure={historyDisclosure}
        note={results.errors.length > 0 ? (
          <div className="card-errors">
            {results.errors.map((err, i) => <p key={i} className="error-line">{err}</p>)}
          </div>
        ) : null}
        done={{
          tone,
          text: `Applied ${results.applied} of ${changes.length} change${changes.length === 1 ? '' : 's'}${proposal.version_name ? ` to ${proposal.version_name}` : ''}${results.failed > 0 ? ` · ${results.failed} failed` : ''}.`,
        }}
      />
    );
  }

  if (dismissed) {
    return (
      <ActionCard icon="trend" title={title} sub={sub} pill={{ state: 'muted', label: 'Kept current' }}
        done={{ tone: 'muted', text: 'Kept the current forecast — nothing was written.' }} />
    );
  }

  return (
    <ActionCard
      icon="trend"
      title={title}
      sub={sub}
      pill={{ state: applying ? 'busy' : 'pending', label: applying ? 'Applying' : 'Needs your approval' }}
      note={proposal.description || null}
      footer={(
        <>
          <Button variant="primary" onClick={handleApply} disabled={noneSelected || applying}>
            {applying ? (<><span className="apply-spinner" />Applying…</>) : `Apply ${selectedCount} change${selectedCount === 1 ? '' : 's'}`}
          </Button>
          <Button variant="ghost" onClick={() => setDismissed(true)} disabled={applying}>Keep current</Button>
        </>
      )}
      footerMeta={proposal.version_name ? `writes to ${proposal.version_name}` : null}
    >
      {/* Past five rows the list scrolls inside the card so Approve stays in
          reach — a 200-account build is skimmed and approved, not read. */}
      <div className={`rp-table-wrap${changes.length > 5 ? ' rp-table-wrap--scroll' : ''}`}>
        <table className="rp-table fcc-table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <Checkbox on={allSelected} onToggle={toggleAll} disabled={applying} label={allSelected ? 'Deselect all' : 'Select all'} />
              </th>
              <th>Account &amp; change</th>
              <th className="is-num">Current</th>
              <th style={{ width: 22 }} />
              <th className="is-accent">Suggested</th>
              <th className="is-num">Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const on = !!selected[idx];
              const change = changes[idx];
              return (
                <tr key={idx} className={on ? '' : 'is-off-row'}>
                  <td><Checkbox on={on} onToggle={() => toggleOne(idx)} disabled={applying} label={row.name} /></td>
                  <td>
                    <span className="rp-cell-title">{row.name}</span>
                    <span className="rp-cell-sub">
                      {row.meta.filter(Boolean).join(' · ')}
                      {(row.from || row.to) && (
                        <>
                          {row.meta.length > 0 && <span aria-hidden="true">·</span>}
                          {row.from && <span className="is-from">{row.from}</span>}
                          {row.from && row.to && <span aria-hidden="true">→</span>}
                          {row.to && <span className="is-to">{row.to}</span>}
                        </>
                      )}
                    </span>
                    {change.reason && <span className="fcc-reason" title={change.reason}>{change.reason}</span>}
                  </td>
                  <td className={`is-num${on ? ' is-strike' : ' is-off'}`}>{row.current != null ? formatCurrency(row.current) : (row.currentLabel || '—')}</td>
                  <td className="fcc-arrow">→</td>
                  <td className={on ? 'is-suggested' : 'is-off'}>{row.suggested != null ? formatCurrency(row.suggested) : (row.suggestedLabel || '—')}</td>
                  <td className={`is-num${on ? '' : ' is-off'}`}>{row.delta != null ? signedCurrency(row.delta) : '—'}</td>
                </tr>
              );
            })}
            {hasMoney && (
              <tr className="is-total">
                <td />
                <td>Net effect</td>
                <td className="is-num">{selectedRows.length} of {rows.length} selected</td>
                <td />
                <td />
                <td className="is-num">{signedCurrency(netDelta)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </ActionCard>
  );
};

export default ForecastChangeCard;
